
import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddLocationMap from './AddLocationMap';
import clientService from 'src/clientService';
import { SiteAdminDetail, LocationCreate } from 'src/api/Api';
import { LocationAdminDetail } from 'src/api/Api';
import { isLocationWithinSiteBoundary } from 'src/mapbox-helpers/location-validation';

interface AddLocationDialogProps {
  open: boolean;
  onClose: () => void;
  site: SiteAdminDetail;
  onLocationAdded: (newLocation: LocationAdminDetail) => void;
}

const AddLocationDialog: React.FC<AddLocationDialogProps> = ({ open, onClose, site, onLocationAdded }) => {
  const [newLocation, setNewLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [lastValidLocation, setLastValidLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationName, setLocationName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBoundary, setShowBoundary] = useState(true);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  useEffect(() => {
    if (newLocation) {
      setLatitude(newLocation.lat.toString());
      setLongitude(newLocation.lng.toString());
    }
  }, [newLocation]);

  // Shared validation function to avoid code duplication
  const validateAndSetLocation = useCallback((
    lngLat: { lng: number; lat: number },
    errorMessage: string,
    resetToLastValid = false
  ) => {
    // Validate location against site boundary + 5km buffer
    if (!isLocationWithinSiteBoundary(lngLat.lat, lngLat.lng, site.boundary, 5)) {
      setError(errorMessage);
      if (resetToLastValid && lastValidLocation) {
        setNewLocation(lastValidLocation);
      } else {
        setNewLocation(null);
      }
      return false;
    }
    setError(null);
    setNewLocation(lngLat);
    setLastValidLocation(lngLat);
    return true;
  }, [site.boundary, lastValidLocation]);

  const handleMapClick = useCallback((lngLat: { lng: number; lat: number }) => {
    validateAndSetLocation(
      lngLat,
      'Location is outside the site boundary (including 5km buffer zone). Please select a location within the highlighted area.'
    );
  }, [validateAndSetLocation]);

  const handleMarkerDrag = useCallback((lngLat: { lng: number; lat: number }) => {
    validateAndSetLocation(
      lngLat,
      'Location is outside the site boundary (including 5km buffer zone). The marker will be reset to the last valid position.',
      true // Reset to last valid location when dragging
    );
  }, [validateAndSetLocation]);

  const handleSaveLocation = async () => {
    if (!newLocation || !locationName) {
      setError('Please select a location on the map and provide a name.');
      return;
    }

    if (!isLocationWithinSiteBoundary(newLocation.lat, newLocation.lng, site.boundary, 5)) {
      setError('Cannot save location: The selected location is outside the site boundary (including 5km buffer zone). Please select a location within the highlighted area.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const locationData: LocationCreate = {
      site_id: site.site_id,
      location_name: locationName,
      location: `POINT (${newLocation.lng} ${newLocation.lat})`,
    };

    try {
      const response = await clientService.api.createApiLocationApiV1AdminLocationsPost(locationData);
      onLocationAdded(response.data);
      onClose();
    } catch (err: any) {
      const error_msg = err?.error?.detail || 'Failed to save the location. Please try again.';
      setError(error_msg);
      console.error(err); 
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewLocation(null);
    setLastValidLocation(null);
    setLocationName('');
    setError(null);
    setLatitude('');
    setLongitude('');
    onClose();
  };

  const handleCoordinateChange = () => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      // Validate location against site boundary + 5km buffer
      if (!isLocationWithinSiteBoundary(lat, lng, site.boundary, 5)) {
        setError('Location is outside the site boundary (including 5km buffer zone). Please enter coordinates within the highlighted area.');
        setNewLocation(null);
        return;
      }
      setError(null);
      setNewLocation({ lat, lng });
      setLastValidLocation({ lat, lng });
    } else {
      setNewLocation(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle>
        Add New Location
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <AddLocationMap
              site={site}
              onMapClick={handleMapClick}
              newLocation={newLocation}
              onMarkerDrag={handleMarkerDrag}
              showBoundary={showBoundary}
            />
          </Box>
          <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
            <FormControlLabel
              control={<Switch checked={showBoundary} onChange={(e) => setShowBoundary(e.target.checked)} />}
              label="Show Boundary"
            />
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {newLocation
                  ? `New Location Coordinates: ${newLocation.lat}, ${newLocation.lng}`
                  : 'Click on the map or enter coordinates to add a new location.'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    id="latitude"
                    label="Latitude"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    onBlur={handleCoordinateChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    id="longitude"
                    label="Longitude"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    onBlur={handleCoordinateChange}
                  />
                </Grid>
              </Grid>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label="Location Name"
                type="text"
                fullWidth
                variant="outlined"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
              />
              <Button
                onClick={handleSaveLocation}
                color="primary"
                variant="contained"
                disabled={isSubmitting || !locationName || !newLocation}
                sx={{ mt: 1 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Save Location'}
              </Button>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddLocationDialog;
